/**
 * UserFilters Component
 *
 * Filter controls for user list
 * Feature: 045-admin-user-tool
 */

import { AdminUserFilters } from '../../graphql/hooks/useAdminUsersGraphQL';

interface UserFiltersProps {
  filters: AdminUserFilters;
  onChange: (filters: AdminUserFilters) => void;
}

export function UserFilters({ filters, onChange }: UserFiltersProps) {
  const handleVerifiedChange = (value: string) => {
    onChange({
      ...filters,
      verified: value === 'all' ? undefined : value === 'true'
    });
  };

  const handleTestUsersChange = (value: string) => {
    onChange({
      ...filters,
      testUsers: value === 'all' ? undefined : value === 'true'
    });
  };

  const handleAiUsersChange = (value: string) => {
    onChange({
      ...filters,
      aiUsers: value === 'all' ? undefined : value === 'true'
    });
  };

  const handleAdminUsersChange = (value: string) => {
    onChange({
      ...filters,
      adminUsers: value === 'all' ? undefined : value === 'true'
    });
  };

  const handleActivityChange = (value: string) => {
    onChange({
      ...filters,
      active: value === 'all' ? undefined : (value as '7d' | '30d' | '90d')
    });
  };

  const hasActiveFilters = filters.verified !== undefined ||
    filters.active ||
    filters.testUsers !== undefined ||
    filters.aiUsers !== undefined ||
    filters.adminUsers !== undefined;

  return (
    <div className="bg-surface-1 rounded-lg border border-border p-4">
      <h3 className="text-sm font-semibold text-foreground mb-3">Filters</h3>

      <div className="space-y-4">
        {/* Verification Status */}
        <div>
          <label className="text-sm text-muted mb-2 block">Verification Status</label>
          <select
            value={filters.verified === undefined ? 'all' : filters.verified.toString()}
            onChange={(e) => handleVerifiedChange(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Users</option>
            <option value="true">Verified Only</option>
            <option value="false">Unverified Only</option>
          </select>
        </div>

        {/* Test Users */}
        <div>
          <label className="text-sm text-muted mb-2 block">Test Users</label>
          <select
            value={filters.testUsers === undefined ? 'all' : filters.testUsers.toString()}
            onChange={(e) => handleTestUsersChange(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Users</option>
            <option value="true">Test Users Only</option>
            <option value="false">Real Users Only</option>
          </select>
        </div>

        {/* AI Users */}
        <div>
          <label className="text-sm text-muted mb-2 block">AI Users</label>
          <select
            value={filters.aiUsers === undefined ? 'all' : filters.aiUsers.toString()}
            onChange={(e) => handleAiUsersChange(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Users</option>
            <option value="true">AI Users Only</option>
            <option value="false">Human Users Only</option>
          </select>
        </div>

        {/* Admin Users */}
        <div>
          <label className="text-sm text-muted mb-2 block">Admin Users</label>
          <select
            value={filters.adminUsers === undefined ? 'all' : filters.adminUsers.toString()}
            onChange={(e) => handleAdminUsersChange(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Users</option>
            <option value="true">Admins Only</option>
            <option value="false">Non-Admins Only</option>
          </select>
        </div>

        {/* Activity Period */}
        <div>
          <label className="text-sm text-muted mb-2 block">Last Active</label>
          <select
            value={filters.active || 'all'}
            onChange={(e) => handleActivityChange(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Time</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={() => onChange({})}
            className="w-full px-3 py-2 text-sm text-muted hover:text-foreground border border-border rounded-md hover:bg-surface-2 transition-colors"
          >
            Clear Filters
          </button>
        )}
      </div>
    </div>
  );
}
